import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  setDoc,
  deleteDoc,
  onSnapshot,
  writeBatch,
  query,
  orderBy,
} from "firebase/firestore";
import {
  ChevronRight,
  Edit2,
  FileText,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  List,
  CheckCircle,
  ChevronLeft,
} from "lucide-react";
import { Helmet } from "react-helmet";
import { Spinner } from "../components";
import { useAuth } from "../hooks/AuthenticationProvider";
import { useNavigate } from "react-router-dom";

const Phases = () => {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [availableForms, setAvailableForms] = useState([]);
  const [isEditingPhase, setIsEditingPhase] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState("");
  const [isAddingPhase, setIsAddingPhase] = useState(false);
  const [isAddingRequirement, setIsAddingRequirement] = useState(false);
  const [editingRequirementId, setEditingRequirementId] = useState(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [newRequirement, setNewRequirement] = useState({
    description: "",
    count: 0,
    frequency: "weekly",
    hasAttachedForm: false,
    attachedFormId: null,
    attachedFormName: null,
  });
  const [saveStatus, setSaveStatus] = useState({
    status: "",
    message: "",
  });
  const [mobileView, setMobileView] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  const { user, isLoading, organizationId } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore();

  useEffect(() => {
    const handleResize = () => {
      setMobileView(window.innerWidth < 768);
      setShowSidebar(window.innerWidth >= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!organizationId) return;

    setLoading(true);
    const phasesRef = collection(db, `organizations/${organizationId}/phases`);
    const q = query(phasesRef, orderBy("order", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const phasesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          name: doc.data().name || doc.id,
        }));
        setPhases(phasesData);

        // If we have a selectedPhaseId, find and set that phase
        if (selectedPhaseId) {
          const updatedSelectedPhase = phasesData.find(
            (phase) => phase.id === selectedPhaseId
          );
          if (updatedSelectedPhase) {
            setSelectedPhase(updatedSelectedPhase);
          }
        }
        // Otherwise, if no phase is selected but phases exist, select the first one
        else if (phasesData.length > 0 && !selectedPhase) {
          setSelectedPhase(phasesData[0]);
          setSelectedPhaseId(phasesData[0].id);
        }

        setLoading(false);
      },
      (error) => {
        console.error("Error fetching phases:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [organizationId, db, selectedPhaseId]);

  useEffect(() => {
    const fetchForms = async () => {
      if (!organizationId) return;

      try {
        const formsRef = collection(
          db,
          `organizations/${organizationId}/forms`
        );
        const formsSnapshot = await getDocs(formsRef);
        const forms = formsSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
          ...doc.data(),
        }));

        setAvailableForms(forms);
      } catch (error) {
        console.error("Error fetching forms:", error);
      }
    };

    fetchForms();
  }, [organizationId]);

  const handleAddPhase = async () => {
    if (!newPhaseName.trim()) {
      setSaveStatus({
        status: "error",
        message: "Phase name cannot be empty",
      });
      return;
    }

    try {
      // Check if the phase name already exists
      if (
        phases.some(
          (phase) => phase.name.toLowerCase() === newPhaseName.toLowerCase()
        )
      ) {
        setSaveStatus({
          status: "error",
          message: "A phase with this name already exists",
        });
        return;
      }

      const phaseId = newPhaseName.replace(/\s+/g, "_").toLowerCase();
      const phaseRef = doc(
        db,
        `organizations/${organizationId}/phases`,
        phaseId
      );

      const newPhase = {
        name: newPhaseName,
        requirements: [],
        order: phases.length + 1,
        createdAt: new Date().toISOString(),
      };

      await setDoc(phaseRef, newPhase);

      setSaveStatus({
        status: "success",
        message: "Phase added successfully",
      });

      setNewPhaseName("");
      setIsAddingPhase(false);

      // Set the newly added phase as selected
      const addedPhase = { id: phaseId, ...newPhase };
      setSelectedPhase(addedPhase);
      setSelectedPhaseId(phaseId);

      // Clear status after a delay
      setTimeout(() => {
        setSaveStatus({ status: "", message: "" });
      }, 3000);
    } catch (error) {
      console.error("Error adding phase:", error);
      setSaveStatus({
        status: "error",
        message: "Failed to add phase: " + error.message,
      });
    }
  };

  const handleReorderPhase = async (phaseId, direction) => {
    try {
      // Find the current phase and its index
      const currentPhaseIndex = phases.findIndex(
        (phase) => phase.id === phaseId
      );
      if (currentPhaseIndex === -1) return;

      // Calculate the target index
      const targetIndex =
        direction === "up"
          ? Math.max(0, currentPhaseIndex - 1)
          : Math.min(phases.length - 1, currentPhaseIndex + 1);

      // If the phase is already at the top/bottom, do nothing
      if (currentPhaseIndex === targetIndex) return;

      // Create a copy of the phases array
      const updatedPhases = [...phases];

      // Swap the phases
      const temp = updatedPhases[currentPhaseIndex];
      updatedPhases[currentPhaseIndex] = updatedPhases[targetIndex];
      updatedPhases[targetIndex] = temp;

      // Update the order property for all phases
      const reorderedPhases = updatedPhases.map((phase, index) => ({
        ...phase,
        order: index + 1,
      }));

      // Create a batch operation correctly using writeBatch
      const batch = writeBatch(db);

      // Add updates to the batch
      reorderedPhases.forEach((phase) => {
        const phaseRef = doc(
          db,
          `organizations/${organizationId}/phases`,
          phase.id
        );
        batch.update(phaseRef, { order: phase.order });
      });

      // Commit the batch
      await batch.commit();

      setSaveStatus({
        status: "success",
        message: "Phase order updated successfully",
      });

      // Clear status after a delay
      setTimeout(() => {
        setSaveStatus({ status: "", message: "" });
      }, 3000);
    } catch (error) {
      console.error("Error reordering phases:", error);
      setSaveStatus({
        status: "error",
        message: "Failed to update phase order: " + error.message,
      });
    }
  };

  const handleSelectPhase = (phase) => {
    setSelectedPhase(phase);
    setSelectedPhaseId(phase.id);
  };

  const handleUpdatePhaseName = async () => {
    if (!newPhaseName.trim()) {
      setSaveStatus({
        status: "error",
        message: "Phase name cannot be empty",
      });
      return;
    }

    try {
      // Check if the phase name already exists (excluding the current phase)
      if (
        phases.some(
          (phase) =>
            phase.id !== selectedPhase.id &&
            phase.name.toLowerCase() === newPhaseName.toLowerCase()
        )
      ) {
        setSaveStatus({
          status: "error",
          message: "A phase with this name already exists",
        });
        return;
      }

      const phaseRef = doc(
        db,
        `organizations/${organizationId}/phases`,
        selectedPhase.id
      );

      await setDoc(
        phaseRef,
        {
          ...selectedPhase,
          name: newPhaseName,
        },
        { merge: true }
      );

      setSaveStatus({
        status: "success",
        message: "Phase name updated successfully",
      });

      setIsEditingPhase(false);

      // Clear status after a delay
      setTimeout(() => {
        setSaveStatus({ status: "", message: "" });
      }, 3000);
    } catch (error) {
      console.error("Error updating phase name:", error);
      setSaveStatus({
        status: "error",
        message: "Failed to update phase name: " + error.message,
      });
    }
  };

  const handleDeletePhase = async (phaseId) => {
    if (!window.confirm("Are you sure you want to delete this phase?")) {
      return;
    }

    try {
      const phaseRef = doc(
        db,
        `organizations/${organizationId}/phases`,
        phaseId
      );
      await deleteDoc(phaseRef);

      setSaveStatus({
        status: "success",
        message: "Phase deleted successfully",
      });

      // If the deleted phase was selected, select another phase
      if (selectedPhase && selectedPhase.id === phaseId) {
        const remainingPhases = phases.filter((phase) => phase.id !== phaseId);
        if (remainingPhases.length > 0) {
          setSelectedPhase(remainingPhases[0]);
          setSelectedPhaseId(remainingPhases[0].id);
        } else {
          setSelectedPhase(null);
          setSelectedPhaseId(null);
        }
      }

      // Clear status after a delay
      setTimeout(() => {
        setSaveStatus({ status: "", message: "" });
      }, 3000);
    } catch (error) {
      console.error("Error deleting phase:", error);
      setSaveStatus({
        status: "error",
        message: "Failed to delete phase: " + error.message,
      });
    }
  };

  const handleAddRequirement = async () => {
    if (!newRequirement.description.trim()) {
      setSaveStatus({
        status: "error",
        message: "Requirement description cannot be empty",
      });
      return;
    }

    try {
      const requirements = [
        ...(selectedPhase.requirements || []),
        {
          id: Date.now().toString(),
          description: newRequirement.description,
          count: parseInt(newRequirement.count, 10) || 0,
          frequency: newRequirement.frequency,
          hasAttachedForm: newRequirement.hasAttachedForm || false,
          attachedFormId: newRequirement.attachedFormId || null,
          attachedFormName: newRequirement.attachedFormName || null,
        },
      ];

      const phaseRef = doc(
        db,
        `organizations/${organizationId}/phases`,
        selectedPhase.id
      );

      await setDoc(
        phaseRef,
        {
          ...selectedPhase,
          requirements,
        },
        { merge: true }
      );

      setSaveStatus({
        status: "success",
        message: "Requirement added successfully",
      });

      setNewRequirement({
        description: "",
        count: 0,
        frequency: "weekly",
      });
      setIsAddingRequirement(false);

      // Clear status after a delay
      setTimeout(() => {
        setSaveStatus({ status: "", message: "" });
      }, 3000);
    } catch (error) {
      console.error("Error adding requirement:", error);
      setSaveStatus({
        status: "error",
        message: "Failed to add requirement: " + error.message,
      });
    }
  };

  const handleUpdateRequirement = async (requirementId) => {
    if (!newRequirement.description.trim()) {
      setSaveStatus({
        status: "error",
        message: "Requirement description cannot be empty",
      });
      return;
    }

    try {
      const requirements = selectedPhase.requirements.map((req) =>
        req.id === requirementId
          ? {
              ...req,
              description: newRequirement.description,
              count: parseInt(newRequirement.count, 10) || 0,
              frequency: newRequirement.frequency,
              hasAttachedForm: newRequirement.hasAttachedForm || false,
              attachedFormId: newRequirement.attachedFormId || null,
              attachedFormName: newRequirement.attachedFormName || null,
            }
          : req
      );

      const phaseRef = doc(
        db,
        `organizations/${organizationId}/phases`,
        selectedPhase.id
      );

      await setDoc(
        phaseRef,
        {
          ...selectedPhase,
          requirements,
        },
        { merge: true }
      );

      setSaveStatus({
        status: "success",
        message: "Requirement updated successfully",
      });

      setNewRequirement({
        description: "",
        count: 0,
        frequency: "weekly",
      });
      setEditingRequirementId(null);

      // Clear status after a delay
      setTimeout(() => {
        setSaveStatus({ status: "", message: "" });
      }, 3000);
    } catch (error) {
      console.error("Error updating requirement:", error);
      setSaveStatus({
        status: "error",
        message: "Failed to update requirement: " + error.message,
      });
    }
  };

  const handleDeleteRequirement = async (requirementId) => {
    if (!window.confirm("Are you sure you want to delete this requirement?")) {
      return;
    }

    try {
      const requirements = selectedPhase.requirements.filter(
        (req) => req.id !== requirementId
      );

      const phaseRef = doc(
        db,
        `organizations/${organizationId}/phases`,
        selectedPhase.id
      );

      await setDoc(
        phaseRef,
        {
          ...selectedPhase,
          requirements,
        },
        { merge: true }
      );

      setSaveStatus({
        status: "success",
        message: "Requirement deleted successfully",
      });

      // Clear status after a delay
      setTimeout(() => {
        setSaveStatus({ status: "", message: "" });
      }, 3000);
    } catch (error) {
      console.error("Error deleting requirement:", error);
      setSaveStatus({
        status: "error",
        message: "Failed to delete requirement: " + error.message,
      });
    }
  };

  const handleEditRequirement = (requirement) => {
    setNewRequirement({
      description: requirement.description,
      count: requirement.count || 0,
      frequency: requirement.frequency || "weekly",
    });
    setEditingRequirementId(requirement.id);
  };

  const handleCancelEdit = () => {
    setNewRequirement({
      description: "",
      count: 0,
      frequency: "weekly",
    });
    setEditingRequirementId(null);
    setIsAddingRequirement(false);
  };

  const formatFrequency = (frequency) => {
    switch (frequency) {
      case "daily":
        return "per day";
      case "weekly":
        return "per week";
      case "monthly":
        return "per month";
      case "total":
        return "total";
      default:
        return frequency;
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>&bull; Program Phases</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-emerald-950/30 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Program Phases
              </h1>
            </div>
          </div>

          {/* Mobile toggle button */}
          {mobileView && (
            <div className="px-6 mb-4">
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className="flex items-center justify-center w-full p-3 bg-slate-800/50 hover:bg-slate-800/70 rounded-lg border border-slate-700/30 text-white transition-all"
              >
                {showSidebar ? (
                  <>
                    <ChevronLeft className="w-5 h-5 mr-2" /> Hide Phase List
                  </>
                ) : (
                  <>
                    <List className="w-5 h-5 mr-2" /> Show Phase List
                  </>
                )}
              </button>
            </div>
          )}

          {/* Status feedback */}
          {saveStatus.status && (
            <div className="px-6 mb-4">
              <div
                className={`flex items-center p-4 rounded-lg ${
                  saveStatus.status === "success"
                    ? "bg-green-500/10 border border-green-500/20 text-green-400"
                    : "bg-red-500/10 border border-red-500/20 text-red-400"
                }`}
              >
                {saveStatus.status === "success" ? (
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0" />
                )}
                <span>{saveStatus.message}</span>
              </div>
            </div>
          )}

          {/* Main content */}
          <div className="flex flex-col md:flex-row">
            {/* Sidebar - Phase List */}
            {showSidebar && (
              <div className="w-full md:w-80 p-6 md:border-r border-slate-800/50 bg-slate-900/20 md:min-h-[calc(100vh-8rem)]">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-white">Phases</h2>
                  <button
                    onClick={() => {
                      setIsAddingPhase(true);
                      setNewPhaseName("");
                    }}
                    className="p-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 rounded-lg transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {isAddingPhase ? (
                  <div className="mb-6 bg-slate-800/50 rounded-xl p-4 border border-indigo-500/30">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-slate-300 mb-1">
                        Phase Name
                      </label>
                      <input
                        type="text"
                        value={newPhaseName}
                        onChange={(e) => setNewPhaseName(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                        placeholder="Phase 1"
                        autoFocus
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setIsAddingPhase(false)}
                        className="px-3 py-1 text-sm font-medium text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddPhase}
                        className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ) : null}

                {phases.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-slate-400 mb-4">No phases created yet</p>
                    <button
                      onClick={() => {
                        setIsAddingPhase(true);
                        setNewPhaseName("");
                      }}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                    >
                      Create First Phase
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {phases.map((phase) => (
                      <div
                        key={phase.id}
                        className={`group relative p-3 rounded-lg cursor-pointer transition-all duration-300 hover:bg-slate-800/50 border ${
                          selectedPhase && selectedPhase.id === phase.id
                            ? "border-indigo-500/50 bg-slate-800/70"
                            : "border-transparent"
                        }`}
                        onClick={() => handleSelectPhase(phase)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <ChevronRight
                              className={`w-4 h-4 mr-2 text-indigo-400 transition-transform ${
                                selectedPhase && selectedPhase.id === phase.id
                                  ? "transform rotate-90"
                                  : ""
                              }`}
                            />
                            <span className="text-slate-200">{phase.name}</span>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {/* Reordering buttons */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReorderPhase(phase.id, "up");
                              }}
                              className="p-1 text-indigo-400 hover:text-indigo-300 rounded transition-colors"
                              disabled={phases.indexOf(phase) === 0}
                              title="Move up"
                            >
                              <ChevronUp
                                className={`w-4 h-4 ${
                                  phases.indexOf(phase) === 0
                                    ? "opacity-50"
                                    : ""
                                }`}
                              />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReorderPhase(phase.id, "down");
                              }}
                              className="p-1 text-indigo-400 hover:text-indigo-300 rounded transition-colors"
                              disabled={
                                phases.indexOf(phase) === phases.length - 1
                              }
                              title="Move down"
                            >
                              <ChevronDown
                                className={`w-4 h-4 ${
                                  phases.indexOf(phase) === phases.length - 1
                                    ? "opacity-50"
                                    : ""
                                }`}
                              />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsEditingPhase(true);
                                setNewPhaseName(phase.name);
                                setSelectedPhase(phase);
                              }}
                              className="p-1 text-indigo-400 hover:text-indigo-300 rounded transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePhase(phase.id);
                              }}
                              className="p-1 text-red-400 hover:text-red-300 rounded transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Main content area - Phase Details */}
            <div className="flex-1 p-6">
              {selectedPhase ? (
                <div className="max-w-3xl mx-auto">
                  {/* Phase Header */}
                  <div className="bg-slate-900/40 backdrop-blur-xl rounded-xl overflow-hidden transition-all duration-500 border border-slate-700/20 shadow-lg mb-6">
                    <div className="relative p-6 border-b border-slate-700/30 bg-slate-800/20">
                      {isEditingPhase ? (
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={newPhaseName}
                              onChange={(e) => setNewPhaseName(e.target.value)}
                              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                              placeholder="Phase Name"
                              autoFocus
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setIsEditingPhase(false)}
                              className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleUpdatePhaseName}
                              className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center">
                          <h2 className="text-2xl font-semibold text-white">
                            {selectedPhase.name}
                          </h2>
                          <button
                            onClick={() => {
                              setIsEditingPhase(true);
                              setNewPhaseName(selectedPhase.name);
                            }}
                            className="p-2 text-indigo-400 hover:text-indigo-300 hover:bg-slate-800/70 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Requirements Section */}
                  <div className="bg-slate-900/40 backdrop-blur-xl rounded-xl overflow-hidden transition-all duration-500 border border-slate-700/20 shadow-lg">
                    <div className="relative p-6 border-b border-slate-700/30 bg-slate-800/20 flex justify-between items-center">
                      <h3 className="text-xl font-semibold text-white">
                        Requirements
                      </h3>
                      <button
                        onClick={() => {
                          setIsAddingRequirement(true);
                          setNewRequirement({
                            description: "",
                            count: 0,
                            frequency: "weekly",
                          });
                        }}
                        className="p-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 rounded-lg transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="p-6">
                      {isAddingRequirement && (
                        <div className="mb-6 bg-slate-800/50 rounded-xl p-4 border border-indigo-500/30">
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-300 mb-1">
                              Requirement Description
                            </label>
                            <input
                              type="text"
                              value={newRequirement.description}
                              onChange={(e) =>
                                setNewRequirement({
                                  ...newRequirement,
                                  description: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors mb-3"
                              placeholder="Attend AA meetings"
                              autoFocus
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                  Count
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  value={newRequirement.count}
                                  onChange={(e) =>
                                    setNewRequirement({
                                      ...newRequirement,
                                      count: e.target.value,
                                    })
                                  }
                                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                  Frequency
                                </label>
                                <select
                                  value={newRequirement.frequency}
                                  onChange={(e) =>
                                    setNewRequirement({
                                      ...newRequirement,
                                      frequency: e.target.value,
                                    })
                                  }
                                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                >
                                  <option value="daily">Daily</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="monthly">Monthly</option>
                                  <option value="total">Total</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <div className="pt-2 mt-2 border-t border-slate-700/50">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newRequirement.hasAttachedForm}
                                onChange={(e) => {
                                  const hasForm = e.target.checked;
                                  setNewRequirement({
                                    ...newRequirement,
                                    hasAttachedForm: hasForm,
                                    attachedFormId: hasForm
                                      ? newRequirement.attachedFormId
                                      : null,
                                    attachedFormName: hasForm
                                      ? newRequirement.attachedFormName
                                      : null,
                                  });
                                }}
                              />
                              <span className="text-sm font-medium text-slate-300">
                                Attach Form
                              </span>
                            </label>
                          </div>
                          {newRequirement.hasAttachedForm && (
                            <div className="mt-3">
                              <label className="block text-sm font-medium text-slate-400 mb-1.5">
                                Select Form
                              </label>
                              <select
                                value={newRequirement.attachedFormId || ""}
                                onChange={(e) => {
                                  const formId = e.target.value;
                                  const selectedForm = availableForms.find(
                                    (f) => f.id === formId
                                  );
                                  setNewRequirement({
                                    ...newRequirement,
                                    attachedFormId: formId,
                                    attachedFormName: selectedForm
                                      ? selectedForm.name
                                      : null,
                                  });
                                }}
                                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                              >
                                <option value="">-- Select a form --</option>
                                {availableForms.map((form) => (
                                  <option key={form.id} value={form.id}>
                                    {form.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div className="flex justify-end gap-2">
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 text-sm font-medium text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleAddRequirement}
                              className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      )}
                      {selectedPhase.requirements &&
                      selectedPhase.requirements.length > 0 ? (
                        <div className="space-y-4">
                          {selectedPhase.requirements.map((requirement) => (
                            <div
                              key={requirement.id}
                              className={`group relative p-4 rounded-xl transition-all duration-300 hover:bg-slate-800/70 border ${
                                editingRequirementId === requirement.id
                                  ? "border-indigo-500/50 bg-slate-800/70"
                                  : "border-slate-700/30"
                              }`}
                            >
                              {editingRequirementId === requirement.id ? (
                                <div>
                                  <div className="mb-4">
                                    <label className="block text-sm font-medium text-slate-300 mb-1">
                                      Requirement Description
                                    </label>
                                    <input
                                      type="text"
                                      value={newRequirement.description}
                                      onChange={(e) =>
                                        setNewRequirement({
                                          ...newRequirement,
                                          description: e.target.value,
                                        })
                                      }
                                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors mb-3"
                                      placeholder="Attend AA meetings"
                                      autoFocus
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-1">
                                          Count
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          value={newRequirement.count}
                                          onChange={(e) =>
                                            setNewRequirement({
                                              ...newRequirement,
                                              count: e.target.value,
                                            })
                                          }
                                          className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-1">
                                          Frequency
                                        </label>
                                        <select
                                          value={newRequirement.frequency}
                                          onChange={(e) =>
                                            setNewRequirement({
                                              ...newRequirement,
                                              frequency: e.target.value,
                                            })
                                          }
                                          className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                        >
                                          <option value="daily">Daily</option>
                                          <option value="weekly">Weekly</option>
                                          <option value="monthly">
                                            Monthly
                                          </option>
                                          <option value="total">Total</option>
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                  {requirement.hasAttachedForm &&
                                    requirement.attachedFormName && (
                                      <p className="text-sm text-indigo-400 mt-1 flex items-center">
                                        <FileText className="w-3 h-3 mr-1" />
                                        Form: {requirement.attachedFormName}
                                      </p>
                                    )}
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={handleCancelEdit}
                                      className="px-3 py-1 text-sm font-medium text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleUpdateRequirement(requirement.id)
                                      }
                                      className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1">
                                      <h4 className="text-base font-medium text-slate-200">
                                        {requirement.description}
                                      </h4>
                                      <div className="flex items-center">
                                        {requirement.count > 0 && (
                                          <p className="text-sm text-indigo-400">
                                            {requirement.count}{" "}
                                            {formatFrequency(
                                              requirement.frequency
                                            )}
                                          </p>
                                        )}
                                        {requirement.hasAttachedForm &&
                                          requirement.attachedFormId && (
                                            <div className="ml-3 px-2 py-0.5 bg-indigo-500/20 rounded-md flex items-center">
                                              <FileText className="w-3 h-3 mr-1 text-indigo-300" />
                                              <span className="text-xs text-indigo-300">
                                                {requirement.attachedFormName ||
                                                  "Form Required"}
                                              </span>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button
                                        onClick={() =>
                                          handleEditRequirement(requirement)
                                        }
                                        className="p-1 text-indigo-400 hover:text-indigo-300 hover:bg-slate-700/50 rounded transition-colors"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleDeleteRequirement(
                                            requirement.id
                                          )
                                        }
                                        className="p-1 text-red-400 hover:text-red-300 hover:bg-slate-700/50 rounded transition-colors"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-slate-400 mb-4">
                            No requirements added yet
                          </p>
                          <button
                            onClick={() => {
                              setIsAddingRequirement(true);
                              setNewRequirement({
                                description: "",
                                count: 0,
                                frequency: "weekly",
                              });
                            }}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                          >
                            Add First Requirement
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[60vh] text-center">
                  <div className="mb-6 p-6 bg-indigo-500/10 rounded-full">
                    <List className="w-12 h-12 text-indigo-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-2">
                    No Phase Selected
                  </h2>
                  <p className="text-slate-400 mb-6 max-w-md">
                    Select a phase from the sidebar or create a new one to get
                    started
                  </p>
                  <button
                    onClick={() => {
                      setIsAddingPhase(true);
                      setNewPhaseName("");
                    }}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                  >
                    Create New Phase
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Phases;
